import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import notify from 'devextreme/ui/notify';
import {
  dataMigrationService,
  MigrationStatus,
  OperationResult,
} from '../../services/dataMigrationService';

const Page = styled.div`
  padding: 1.5rem;
  max-width: 960px;
`;

const Section = styled.section`
  margin-bottom: 1.5rem;
  padding: 1rem;
  border: 1px solid #dfe1e6;
  border-radius: 8px;
  background: #fff;
`;

const Title = styled.h2`
  margin: 0 0 0.5rem;
  font-size: 1.25rem;
`;

const Hint = styled.p`
  margin: 0 0 1rem;
  color: #5e6c84;
  font-size: 0.9rem;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.75rem;
`;

const ActionButton = styled.button<{ $danger?: boolean }>`
  padding: 0.5rem 1rem;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  background: ${({ $danger }) => ($danger ? '#de350b' : '#0052cc')};
  color: #fff;
  font-weight: 600;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;

  th, td {
    border-bottom: 1px solid #ebecf0;
    padding: 0.4rem 0.5rem;
    text-align: left;
  }
`;

const ErrorList = styled.ul`
  color: #de350b;
  margin: 0.5rem 0 0;
  padding-left: 1.25rem;
`;

const DataMigrationPage: React.FC = () => {
  const [status, setStatus] = useState<MigrationStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState<OperationResult | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dataMigrationService.getStatus();
      if (response.status === 1) {
        setStatus(response.data);
      } else {
        notify(typeof response.data === 'string' ? response.data : 'No disponible', 'warning', 3000);
      }
    } catch {
      notify('No se pudo cargar el estado de migración.', 'error', 3000);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const runAction = async (
    label: string,
    phrase: string,
    action: () => Promise<{ status: number; data: OperationResult | string }>,
  ) => {
    const typed = window.prompt(`Escriba "${phrase}" para confirmar ${label}:`);
    if (typed !== phrase) {
      notify('Confirmación cancelada o incorrecta.', 'warning', 2500);
      return;
    }

    setLoading(true);
    try {
      const response = await action();
      if (response.status === 1 && typeof response.data !== 'string') {
        setLastResult(response.data);
        notify(`${label} completado.`, 'success', 3000);
        await refresh();
      } else {
        const msg = typeof response.data === 'string' ? response.data : 'Error en la operación';
        notify(msg, 'error', 4000);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error inesperado';
      notify(msg, 'error', 4000);
    } finally {
      setLoading(false);
    }
  };

  const schemaOk = status?.schema?.isValid ?? false;

  return (
    <Page>
      <Title>Migración Dev → Prod (interno Idare)</Title>
      <Hint>
        Ruta oculta para pruebas locales. Requiere Admin, API en DEV y{' '}
        <code>DATA_MIGRATION_ENABLED=true</code>.
      </Hint>

      <Section>
        <Title as="h3">Estado</Title>
        <p>DEV bloqueado: <strong>{status?.devLocked ? 'Sí' : 'No'}</strong></p>
        <p>Operación en curso: <strong>{status?.isRunning ? 'Sí' : 'No'}</strong></p>
        {status?.lastOperation && (
          <p>Última: {status.lastOperation} — {status.lastMessage}</p>
        )}
        <ButtonRow>
          <ActionButton type="button" onClick={refresh} disabled={loading}>
            Actualizar
          </ActionButton>
          <ActionButton
            type="button"
            onClick={async () => {
              setLoading(true);
              try {
                const response = await dataMigrationService.validateSchema();
                if (response.status === 1) {
                  notify('Esquema válido.', 'success', 2500);
                } else if (typeof response.data !== 'string' && response.data.errors?.length) {
                  notify(response.data.errors.join(' | '), 'error', 5000);
                }
                await refresh();
              } catch {
                notify('Error al validar esquema.', 'error', 3000);
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
          >
            Validar esquema
          </ActionButton>
        </ButtonRow>
        {status?.schema && !status.schema.isValid && (
          <ErrorList>
            {status.schema.errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ErrorList>
        )}
      </Section>

      <Section>
        <Title as="h3">Conteos por tabla</Title>
        <Table>
          <thead>
            <tr>
              <th>Tabla</th>
              <th>DEV</th>
              <th>PROD</th>
            </tr>
          </thead>
          <tbody>
            {(status?.tableCounts ?? []).map((row) => (
              <tr key={row.table}>
                <td>{row.table}</td>
                <td>{row.devCount}</td>
                <td>{row.prodCount}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Section>

      <Section>
        <Title as="h3">Acciones</Title>
        <Hint>Primera migración: limpiar PROD antes si PROD ya tiene filas.</Hint>
        <ButtonRow>
          <ActionButton
            type="button"
            $danger
            disabled={loading || !schemaOk}
            onClick={() => runAction('limpieza PROD', 'LIMPIAR-PROD', () => dataMigrationService.cleanProd('LIMPIAR-PROD'))}
          >
            Limpiar PROD (datos)
          </ActionButton>
          <ActionButton
            type="button"
            disabled={loading || !schemaOk}
            onClick={() => runAction('primera migración', 'MIGRAR-PROD', () => dataMigrationService.fullMigrate('MIGRAR-PROD'))}
          >
            Primera migración
          </ActionButton>
          <ActionButton
            type="button"
            disabled={loading || !schemaOk}
            onClick={() => runAction('sync', 'SYNC-PROD', () => dataMigrationService.sync('SYNC-PROD', false))}
          >
            Sync faltantes
          </ActionButton>
          <ActionButton
            type="button"
            $danger
            disabled={loading || !schemaOk}
            onClick={() => runAction('limpieza DEV', 'LIMPIAR-DEV', () => dataMigrationService.cleanDev('LIMPIAR-DEV'))}
          >
            Limpiar DEV + bloquear
          </ActionButton>
        </ButtonRow>
      </Section>

      {lastResult && (
        <Section>
          <Title as="h3">Resultado</Title>
          <p>{lastResult.message}</p>
          <Table>
            <thead>
              <tr>
                <th>Tabla</th>
                <th>Filas</th>
              </tr>
            </thead>
            <tbody>
              {lastResult.tables.map((t) => (
                <tr key={t.table}>
                  <td>{t.table}</td>
                  <td>{t.rowsAffected}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Section>
      )}
    </Page>
  );
};

export default DataMigrationPage;
